import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, Truck, Wrench, Clock, 
  AlertTriangle, Calendar, BarChart3, PieChart, Activity, Target,
  ChevronRight, Loader2
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary' }) {
  const colorMap = {
    primary: 'var(--primary)',
    success: 'var(--success)',
    warning: 'var(--warning)',
    error: 'var(--error)',
  };
  
  return (
    <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-md" style={{ background: `${colorMap[color]}20` }}>
          <Icon size={18} style={{ color: colorMap[color] }} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
            {trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>
        {value}
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{title}</p>
      {subtitle && <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-faint)' }}>{subtitle}</p>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-md" style={{ background: 'var(--primary-muted)' }}>
        <Icon size={18} style={{ color: 'var(--primary)' }} />
      </div>
      <div>
        <h2 className="font-['Manrope'] font-bold text-base" style={{ color: 'var(--text-primary)' }}>{title}</h2>
        {subtitle && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>}
      </div>
    </div>
  );
}

export default function CustomerAnalytics() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('fleet');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    fleet: null,
    financial: null,
    operational: null,
    predictive: null,
  });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [fleetRes, financialRes, operationalRes, predictiveRes] = await Promise.all([
          fetch(`${API}/api/analytics/customer/fleet-performance`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/analytics/customer/financial`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/analytics/customer/operational`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/analytics/customer/predictive`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        
        setData({
          fleet: fleetRes.ok ? await fleetRes.json() : null,
          financial: financialRes.ok ? await financialRes.json() : null,
          operational: operationalRes.ok ? await operationalRes.json() : null,
          predictive: predictiveRes.ok ? await predictiveRes.json() : null,
        });
      } catch (err) {
        console.error('Analytics fetch error:', err);
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const tabs = [
    { id: 'fleet', label: 'Fleet', icon: Truck },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'operational', label: 'Operations', icon: Activity },
    { id: 'predictive', label: 'Forecast', icon: Target },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold font-['Manrope']" style={{ color: 'var(--text-primary)' }}>
          Advanced Analytics
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          Deep insights into your fleet performance and costs
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="analytics-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id ? 'bg-[var(--primary)] text-white' : ''
            }`}
            style={activeTab !== tab.id ? { background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' } : {}}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Fleet Performance Tab */}
      {activeTab === 'fleet' && data.fleet && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard 
              title="Cost Per Mile" 
              value={`$${data.fleet.summary.cost_per_mile.toFixed(3)}`}
              subtitle="vs industry $0.210"
              icon={DollarSign}
              color={data.fleet.summary.cost_per_mile < 0.21 ? 'success' : 'warning'}
            />
            <StatCard 
              title="Total Repairs" 
              value={data.fleet.summary.total_repairs}
              subtitle={`${data.fleet.summary.fleet_size} units`}
              icon={Wrench}
              color="primary"
            />
            <StatCard 
              title="Avg Downtime" 
              value={`${data.fleet.summary.avg_downtime_days} days`}
              subtitle="per repair"
              icon={Clock}
              color={data.fleet.summary.avg_downtime_days < 3 ? 'success' : 'warning'}
            />
            <StatCard 
              title="In Shop Now" 
              value={data.fleet.summary.units_in_shop}
              subtitle="units"
              icon={AlertTriangle}
              color={data.fleet.summary.units_in_shop > 2 ? 'error' : 'success'}
            />
          </div>

          {/* PM vs Breakdown Ratio */}
          <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <SectionHeader icon={PieChart} title="Preventive vs Breakdown" subtitle="Repair type distribution" />
            <div className="flex items-center gap-6">
              <div className="w-32 h-32">
                <ResponsiveContainer>
                  <RePieChart>
                    <Pie
                      data={[
                        { name: 'Preventive', value: data.fleet.repair_ratio.preventive },
                        { name: 'Breakdown', value: data.fleet.repair_ratio.breakdown },
                      ]}
                      innerRadius={30}
                      outerRadius={50}
                      dataKey="value"
                    >
                      <Cell fill="#10B981" />
                      <Cell fill="#EF4444" />
                    </Pie>
                  </RePieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Preventive: {data.fleet.repair_ratio.preventive}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Breakdown: {data.fleet.repair_ratio.breakdown}</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                  Ratio: {data.fleet.repair_ratio.ratio}:1 (higher is better)
                </p>
              </div>
            </div>
          </div>

          {/* Cost Trend */}
          {data.fleet.cost_trend.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={TrendingUp} title="Cost Trend" subtitle="Monthly maintenance spend" />
              <div className="h-48">
                <ResponsiveContainer>
                  <LineChart data={data.fleet.cost_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => `$${v}`} />
                    <Tooltip 
                      contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']}
                    />
                    <Line type="monotone" dataKey="cost" stroke="var(--primary)" strokeWidth={2} dot={{ fill: 'var(--primary)' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Units by Repairs */}
          {data.fleet.repair_by_unit.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={Truck} title="Units by Repair Frequency" subtitle="Most serviced units" />
              <div className="space-y-2">
                {data.fleet.repair_by_unit.slice(0, 5).map((unit, i) => (
                  <div key={unit.unit_id} className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--bg)' }}>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {unit.unit_number || unit.unit_id}
                    </span>
                    <span className="text-xs font-mono px-2 py-1 rounded" style={{ background: 'var(--primary-muted)', color: 'var(--primary)' }}>
                      {unit.repairs} repairs
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Financial Tab */}
      {activeTab === 'financial' && data.financial && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard 
              title="Total Spend" 
              value={`$${data.financial.summary.total_spend.toLocaleString()}`}
              subtitle="all time"
              icon={DollarSign}
              color="primary"
            />
            <StatCard 
              title="Avg Monthly" 
              value={`$${data.financial.summary.avg_monthly.toLocaleString()}`}
              icon={BarChart3}
              color="primary"
            />
            <StatCard 
              title="YTD Spend" 
              value={`$${data.financial.summary.ytd_current.toLocaleString()}`}
              trend={data.financial.summary.ytd_change_pct > 0 ? 'up' : 'down'}
              trendValue={`${Math.abs(data.financial.summary.ytd_change_pct)}%`}
              icon={TrendingUp}
              color={data.financial.summary.ytd_change_pct > 10 ? 'warning' : 'success'}
            />
            <StatCard 
              title="Unpaid Invoices" 
              value={data.financial.unpaid_count}
              icon={AlertTriangle}
              color={data.financial.unpaid_count > 5 ? 'error' : 'success'}
            />
          </div>

          {/* Monthly Spend Chart */}
          {data.financial.monthly_spend.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={BarChart3} title="Monthly Spend" subtitle="Last 12 months" />
              <div className="h-48">
                <ResponsiveContainer>
                  <BarChart data={data.financial.monthly_spend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => `$${v}`} />
                    <Tooltip 
                      contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Spend']}
                    />
                    <Bar dataKey="amount" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Category Breakdown */}
          {data.financial.category_breakdown.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={PieChart} title="Spend by Category" />
              <div className="grid md:grid-cols-2 gap-4">
                <div className="h-48">
                  <ResponsiveContainer>
                    <RePieChart>
                      <Pie
                        data={data.financial.category_breakdown.slice(0, 6)}
                        dataKey="amount"
                        nameKey="category"
                        innerRadius={40}
                        outerRadius={70}
                        label={({ category, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {data.financial.category_breakdown.slice(0, 6).map((entry, i) => (
                          <Cell key={entry.category} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {data.financial.category_breakdown.slice(0, 6).map((cat, i) => (
                    <div key={cat.category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{cat.category}</span>
                      </div>
                      <span className="text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                        ${cat.amount.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Invoice Aging */}
          <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <SectionHeader icon={Calendar} title="Invoice Aging" subtitle="Outstanding amounts" />
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(data.financial.invoice_aging).map(([bucket, amount]) => (
                <div key={bucket} className="text-center p-3 rounded-md" style={{ background: 'var(--bg)' }}>
                  <p className="text-lg font-bold font-mono" style={{ color: amount > 0 ? 'var(--warning)' : 'var(--text-primary)' }}>
                    ${amount.toLocaleString()}
                  </p>
                  <p className="text-[10px] uppercase" style={{ color: 'var(--text-faint)' }}>
                    {bucket.replace('_', ' ')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Operational Tab */}
      {activeTab === 'operational' && data.operational && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard 
              title="Total Repairs" 
              value={data.operational.summary.total_repairs}
              icon={Wrench}
              color="primary"
            />
            <StatCard 
              title="Avg Turnaround" 
              value={`${data.operational.summary.avg_turnaround_hours}h`}
              subtitle={`${data.operational.summary.avg_turnaround_days} days`}
              icon={Clock}
              color={data.operational.summary.avg_turnaround_hours < 48 ? 'success' : 'warning'}
            />
            <StatCard 
              title="Completed" 
              value={data.operational.summary.completed_repairs}
              icon={Activity}
              color="success"
            />
            <StatCard 
              title="Pending" 
              value={data.operational.summary.pending_repairs}
              icon={AlertTriangle}
              color={data.operational.summary.pending_repairs > 5 ? 'warning' : 'success'}
            />
          </div>

          {/* Top Repair Types */}
          {data.operational.top_repair_types.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={Wrench} title="Common Repair Types" />
              <div className="h-48">
                <ResponsiveContainer>
                  <BarChart data={data.operational.top_repair_types.slice(0, 6)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis dataKey="type" type="category" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} width={80} />
                    <Tooltip contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)' }} />
                    <Bar dataKey="count" fill="var(--primary)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Vendor Performance */}
          {data.operational.vendor_performance.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={Activity} title="Vendor Performance" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th className="text-left py-2 px-2" style={{ color: 'var(--text-muted)' }}>Vendor</th>
                      <th className="text-right py-2 px-2" style={{ color: 'var(--text-muted)' }}>Repairs</th>
                      <th className="text-right py-2 px-2" style={{ color: 'var(--text-muted)' }}>Avg Time</th>
                      <th className="text-right py-2 px-2" style={{ color: 'var(--text-muted)' }}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.operational.vendor_performance.slice(0, 5).map(v => (
                      <tr key={v.vendor_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="py-2 px-2" style={{ color: 'var(--text-primary)' }}>{v.vendor_name}</td>
                        <td className="text-right py-2 px-2 font-mono" style={{ color: 'var(--text-muted)' }}>{v.total_repairs}</td>
                        <td className="text-right py-2 px-2 font-mono" style={{ color: 'var(--text-muted)' }}>{v.avg_turnaround_hours}h</td>
                        <td className="text-right py-2 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${v.completion_rate >= 80 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {v.completion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Predictive Tab */}
      {activeTab === 'predictive' && data.predictive && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard 
              title="PM Due 30 Days" 
              value={data.predictive.summary.pm_due_30_days}
              icon={Calendar}
              color={data.predictive.summary.pm_due_30_days > 3 ? 'warning' : 'success'}
            />
            <StatCard 
              title="DOT Due 30 Days" 
              value={data.predictive.summary.dot_due_30_days}
              icon={AlertTriangle}
              color={data.predictive.summary.dot_due_30_days > 0 ? 'error' : 'success'}
            />
            <StatCard 
              title="High Frequency Units" 
              value={data.predictive.summary.high_frequency_units}
              subtitle="3+ repairs in 90 days"
              icon={Truck}
              color={data.predictive.summary.high_frequency_units > 0 ? 'warning' : 'success'}
            />
            <StatCard 
              title="Fleet Size" 
              value={data.predictive.summary.fleet_size}
              icon={Truck}
              color="primary"
            />
          </div>

          {/* Cost Forecast */}
          <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <SectionHeader icon={Target} title="Cost Forecast" subtitle="Based on 6-month historical average" />
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center p-4 rounded-md" style={{ background: 'var(--bg)' }}>
                <p className="text-2xl font-bold font-mono" style={{ color: 'var(--primary)' }}>
                  ${data.predictive.forecast.projected_next_month.toLocaleString()}
                </p>
                <p className="text-xs uppercase mt-1" style={{ color: 'var(--text-faint)' }}>Next Month</p>
              </div>
              <div className="text-center p-4 rounded-md" style={{ background: 'var(--bg)' }}>
                <p className="text-2xl font-bold font-mono" style={{ color: 'var(--primary)' }}>
                  ${data.predictive.forecast.projected_next_quarter.toLocaleString()}
                </p>
                <p className="text-xs uppercase mt-1" style={{ color: 'var(--text-faint)' }}>Next Quarter</p>
              </div>
              <div className="text-center p-4 rounded-md" style={{ background: 'var(--bg)' }}>
                <p className="text-2xl font-bold font-mono" style={{ color: 'var(--primary)' }}>
                  ${data.predictive.forecast.projected_next_year.toLocaleString()}
                </p>
                <p className="text-xs uppercase mt-1" style={{ color: 'var(--text-faint)' }}>Next Year</p>
              </div>
            </div>
          </div>

          {/* Upcoming PM */}
          {data.predictive.pm_due_soon.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={Calendar} title="Upcoming PM Services" />
              <div className="space-y-2">
                {data.predictive.pm_due_soon.slice(0, 5).map(pm => (
                  <div key={pm.unit_id} className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--bg)' }}>
                    <div>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {pm.unit_number}
                      </span>
                      <span className="text-xs ml-2" style={{ color: 'var(--text-faint)' }}>
                        {new Date(pm.due_date).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      pm.urgency === 'critical' ? 'bg-red-500/20 text-red-400' :
                      pm.urgency === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {pm.days_until} days
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Units Needing Attention */}
          {data.predictive.units_needing_attention.length > 0 && (
            <div className="p-4 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <SectionHeader icon={AlertTriangle} title="Units Needing Attention" subtitle="High repair frequency (3+ in 90 days)" />
              <div className="space-y-2">
                {data.predictive.units_needing_attention.map(unit => (
                  <div key={unit.unit_id} className="flex items-center justify-between p-2 rounded-md" style={{ background: 'var(--bg)' }}>
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {unit.unit_number}
                    </span>
                    <span className="text-xs font-mono px-2 py-1 rounded bg-red-500/20 text-red-400">
                      {unit.repairs_90_days} repairs
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
